import React from "react";
import axios from "axios";
import { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis, ReferenceArea, ReferenceLine, Tooltip, CartesianGrid, Label } from "recharts";
import { Brand } from "@/components/Layout";
import { DIM_LEVELS, QICM_T } from "@/data/qicm";
import { computeQicmResults, qicmClinicalReading, qicmRecommendations } from "@/lib/qicmScoring";
import { PatientClosing } from "@/components/patient/PatientClosing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SectionTitle = ({ children, sub }) => (
  <div className="text-center mb-6">
    <h3 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)]">{children}</h3>
    {sub && <p className="text-sm text-[var(--ink-soft)] mt-1">{sub}</p>}
  </div>
);

const DimensionBar = ({ row }) => (
  <div className="rounded-2xl bg-white/70 border border-[var(--border)] p-5 print-break" data-testid={`qicm-dim-${row.key}`}>
    <div className="flex items-baseline justify-between mb-1">
      <h4 className="font-serif-title text-2xl" style={{ color: row.color }}>{row.name}</h4>
      <span className="font-semibold text-[var(--ink)]">{row.score}/40</span>
    </div>
    <p className="text-xs text-[var(--ink-soft)] mb-3">{row.description}</p>
    <div className="h-2.5 w-full rounded-full bg-[var(--beige)] overflow-hidden mb-2">
      <div className="h-full rounded-full" style={{ width: `${(row.score / 40) * 100}%`, backgroundColor: row.color }} />
    </div>
    <span className="inline-block rounded-full px-2.5 py-0.5 text-xs text-white" style={{ backgroundColor: row.level.color }}>{row.level.label}</span>
  </div>
);

const IicGauge = ({ iic, level }) => {
  const pct = ((iic + 40) / 80) * 100;
  return (
    <div data-testid="qicm-iic">
      <div className="relative h-3 rounded-full mb-3" style={{ background: "linear-gradient(90deg,#B0553B,#E99F4F,#D4A77B,#B58C67,#8A6D56)" }}>
        <div className="absolute -top-1.5 h-6 w-1.5 rounded-full bg-[var(--ink)]" style={{ left: `calc(${pct}% - 3px)` }} />
      </div>
      <div className="flex justify-between text-[0.65rem] text-[var(--ink-soft)] mb-3"><span>-40</span><span>0</span><span>+40</span></div>
      <div className="text-center">
        <span className="font-serif-title text-5xl text-[var(--gold)]">{iic > 0 ? "+" : ""}{iic}</span>
        <span className="ml-3 inline-block rounded-full px-3 py-0.5 text-sm text-white align-middle" style={{ backgroundColor: level.color }}>{level.label}</span>
      </div>
    </div>
  );
};

const ProfileMap = ({ reflexivite, certitude, profile, t }) => (
  <ResponsiveContainer width="100%" height={360}>
    <ScatterChart margin={{ top: 16, right: 20, bottom: 24, left: 8 }}>
      <CartesianGrid stroke="#e7ddd0" />
      <XAxis type="number" dataKey="x" name={t.axisRefl} domain={[0, 40]} ticks={[0, 10, 20, 30, 40]} tick={{ fill: "#6E5C53", fontSize: 11 }}>
        <Label value={t.axisRefl} position="insideBottom" offset={-14} fill="#6E5C53" fontSize={12} />
      </XAxis>
      <YAxis type="number" dataKey="y" name={t.axisCert} domain={[0, 40]} ticks={[0, 10, 20, 30, 40]} tick={{ fill: "#6E5C53", fontSize: 11 }}>
        <Label value={t.axisCert} angle={-90} position="insideLeft" fill="#6E5C53" fontSize={12} />
      </YAxis>
      <ZAxis range={[260, 260]} />
      <ReferenceArea x1={0} x2={20.5} y1={20.5} y2={40} fill="#B0553B" fillOpacity={0.09} label={{ value: t.quadC, fill: "#B0553B", fontSize: 12, fontWeight: 600 }} />
      <ReferenceArea x1={20.5} x2={40} y1={20.5} y2={40} fill="#C08552" fillOpacity={0.09} label={{ value: t.quadB, fill: "#C08552", fontSize: 12, fontWeight: 600 }} />
      <ReferenceArea x1={0} x2={20.5} y1={0} y2={20.5} fill="#8A9A6D" fillOpacity={0.09} label={{ value: t.quadD, fill: "#71805A", fontSize: 12, fontWeight: 600 }} />
      <ReferenceArea x1={20.5} x2={40} y1={0} y2={20.5} fill="#8A6D56" fillOpacity={0.09} label={{ value: t.quadA, fill: "#8A6D56", fontSize: 12, fontWeight: 600 }} />
      <ReferenceLine x={20.5} stroke="#a1907f" strokeDasharray="4 4" />
      <ReferenceLine y={20.5} stroke="#a1907f" strokeDasharray="4 4" />
      <Tooltip cursor={{ strokeDasharray: "3 3" }} formatter={(v, n) => [`${v}/40`, n]} />
      <Scatter name={t.yourPosition} data={[{ x: reflexivite, y: certitude }]} fill={profile.color} stroke="#4A3B36" strokeWidth={1.5} />
    </ScatterChart>
  </ResponsiveContainer>
);

export const QicmResults = ({ answers, onRestart, lang = "fr" }) => {
  const t = QICM_T[lang];
  const results = React.useMemo(() => computeQicmResults(answers, lang), [answers, lang]);
  const reading = React.useMemo(() => qicmClinicalReading(results, lang), [results, lang]);
  const recs = React.useMemo(() => qicmRecommendations(results, answers, lang), [results, answers, lang]);
  const { profile } = results;

  React.useEffect(() => {
    axios.post(`${API}/qicm-evaluations`, { answers }).catch((e) => console.error("save qicm failed", e));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="animate-fade-up print-container" data-testid="qicm-results-view">
      <div className="hidden print:block mb-6"><Brand /></div>

      <div className="text-center mb-10">
        <h2 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-4">{t.thanks}</h2>
        <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto">{t.thanksText}</p>
      </div>

      <div className="mb-10">
        <SectionTitle sub={t.dimsSub}>{t.dimsTitle}</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {results.rows.map((row) => (<DimensionBar key={row.key} row={row} />))}
        </div>
      </div>

      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-5 sm:p-7 mb-10 print-break" data-testid="qicm-profile-map">
        <SectionTitle sub={t.mapSub}>{t.mapTitle}</SectionTitle>
        <ProfileMap reflexivite={results.reflexivite} certitude={results.certitude} profile={results.profile} t={t} />
      </div>

      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 mb-6 print-break">
        <SectionTitle sub={t.iicSub}>{t.iicTitle}</SectionTitle>
        <IicGauge iic={results.iic} level={results.iicLevel} />
      </div>

      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 mb-10 print-break" data-testid="qicm-ifc">
        <SectionTitle sub={t.ifcSub}>{t.ifcTitle}</SectionTitle>
        {results.ifc == null ? (
          <p className="text-center text-[var(--ink-soft)]">{t.ifcNA}</p>
        ) : (
          <div className="text-center">
            <div className="h-3 w-full rounded-full bg-[var(--beige)] overflow-hidden mb-3">
              <div className="h-full rounded-full" style={{ width: `${results.ifc}%`, backgroundColor: results.ifcLevel.color }} />
            </div>
            <span className="font-serif-title text-5xl text-[var(--gold)]">{results.ifc}<span className="text-2xl text-[var(--ink-soft)]"> %</span></span>
            <span className="ml-3 inline-block rounded-full px-3 py-0.5 text-sm text-white align-middle" style={{ backgroundColor: results.ifcLevel.color }}>{results.ifcLevel.label}</span>
          </div>
        )}
      </div>

      <div className="rounded-3xl border-2 p-6 sm:p-8 mb-10 print-break" style={{ borderColor: profile.color }} data-testid="qicm-profile">
        <SectionTitle sub={profile.axis}>{t.profilePrefix} {profile.code} — {profile.name}</SectionTitle>
        <p className="text-[var(--ink)] leading-relaxed mb-4">{profile.caracteristiques}</p>
        <p className="text-sm"><span className="font-semibold" style={{ color: profile.color }}>{profile.noteLabel} : </span><span className="text-[var(--ink-soft)]">{profile.note}</span></p>
      </div>

      <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-5 sm:p-7 mb-10 print-break" data-testid="qicm-interpretation">
        <SectionTitle sub={t.interpSub}>{t.interpTitle}</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {DIM_LEVELS.map((r) => (
            <div key={r.min} className="flex items-center gap-3 rounded-xl bg-[var(--sand)] border border-[var(--border)] p-3">
              <span className="inline-flex items-center justify-center h-9 w-16 rounded-lg text-white text-sm font-semibold shrink-0" style={{ backgroundColor: r.color }}>{r.min}–{r.max}</span>
              <span className="text-sm text-[var(--ink)]">{r[lang].m}</span>
            </div>
          ))}
        </div>
      </div>

      {results.flaggedItems.length > 0 && (
        <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-5 sm:p-7 mb-10 print-break" data-testid="qicm-flagged">
          <SectionTitle sub={t.flaggedSub}>{t.flaggedTitle}</SectionTitle>
          <ul className="space-y-2">
            {results.flaggedItems.map((f) => (
              <li key={f.n} className="flex items-start gap-3 text-sm text-[var(--ink)]">
                <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[var(--gold)] text-white text-xs shrink-0">{f.n}</span>
                <span>{f.note} <span className="text-[var(--ink-soft)]">({f.score}/4)</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-3xl bg-[var(--gold)] text-white p-6 sm:p-9 mb-10 print-break">
        <h3 className="font-serif-title text-3xl mb-4">{t.clinicalTitle}</h3>
        <p className="leading-relaxed text-[0.98rem]" data-testid="qicm-clinical-reading">{reading}</p>
        {recs.length > 0 && (
          <>
            <p className="font-semibold mt-5 mb-2">{t.recoTitle}</p>
            <ul className="list-disc pl-5 space-y-1 text-[0.95rem] text-white/95">
              {recs.map((r, i) => (<li key={i}>{r}</li>))}
            </ul>
          </>
        )}
      </div>

      <PatientClosing lang={lang} />

      <div className="flex flex-wrap items-center justify-center gap-4 no-print">
        <button onClick={() => window.print()} data-testid="qicm-btn-export-pdf" className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all">{t.exportPdf}</button>
        <button onClick={onRestart} data-testid="qicm-btn-restart" className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">{t.restart}</button>
      </div>
    </div>
  );
};
